import {testIdProps} from '@github-ui/test-id-props'
import {memo, useEffect, useRef} from 'react'
import type {ColumnInstance} from 'react-table'

import type {MemexItemModel} from '../../models/memex-item-model'
import {usePaginatedMemexItemsQuery} from '../../state-providers/memex-items/queries/use-paginated-memex-items-query'
import useIsVisible from '../board/hooks/use-is-visible'
import {StyledTableCell} from './table-cell'
import {useTableInstance} from './table-provider'
import {StyledTableRow, tableRowStyle} from './table-row'

export const PLACEHOLDER_ROW_COUNT = 5

export const TablePagination: React.FC<{groupId?: string}> = ({groupId}) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const {isVisible} = useIsVisible({ref})
  const prevIsVisible = useRef<boolean>(false)
  const {
    isFetchingNextPage,
    isFetchingNextPageForGroup,
    fetchNextPage,
    fetchNextPageForGroup,
    hasNextPage,
    hasNextPageForGroup,
  } = usePaginatedMemexItemsQuery()
  useEffect(() => {
    // isVisible remains briefly true after fetchNextPage completes.
    // This check prevents an extra request from being fired.
    if (prevIsVisible.current === isVisible) return
    prevIsVisible.current = isVisible
    if (!isVisible) return
    if (groupId && hasNextPageForGroup(groupId) && !isFetchingNextPageForGroup(groupId)) {
      fetchNextPageForGroup(groupId)
    } else if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [
    isVisible,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    groupId,
    fetchNextPageForGroup,
    hasNextPageForGroup,
    isFetchingNextPageForGroup,
  ])
  const {visibleColumns} = useTableInstance()

  return (
    <div ref={ref} {...testIdProps(`table-pagination${groupId ? `-${groupId}` : ''}`)}>
      {!(groupId ? hasNextPageForGroup(groupId) : hasNextPage)
        ? null
        : [...Array(PLACEHOLDER_ROW_COUNT).keys()].map(key => <PlaceholderRow key={key} columns={visibleColumns} />)}
    </div>
  )
}

type PlaceholderRowProps = {
  columns: Array<ColumnInstance<MemexItemModel>>
}

const PlaceholderRowUnmemoized: React.FC<PlaceholderRowProps> = ({columns}) => {
  return (
    <StyledTableRow role="row" style={{...tableRowStyle}} {...testIdProps('placeholder-row')}>
      {columns.map(column => (
        <StyledTableCell key={column.id} role="gridcell" style={{whiteSpace: 'nowrap', width: column.width}}>
          {column.Placeholder}
        </StyledTableCell>
      ))}
    </StyledTableRow>
  )
}

const PlaceholderRow = memo(PlaceholderRowUnmemoized)

try{ TablePagination.displayName ||= 'TablePagination' } catch {}
try{ PlaceholderRowUnmemoized.displayName ||= 'PlaceholderRowUnmemoized' } catch {}
try{ PlaceholderRow.displayName ||= 'PlaceholderRow' } catch {}